import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import styled from "styled-components";

import { useStores } from "../../stores/useStores";
import { color } from "../../theme/color";
import { Button, IconButton, KeyBadge } from "../ui/controls";
import { Icon } from "../ui/Icon";
import { DisplayPanel } from "./DisplayPanel";
import { SetlistMenu } from "./SetlistMenu";
import { SheetView } from "./SheetView";
import { SongTreeMenu } from "./SongTreeMenu";
import { useHoldChord } from "./useHoldChord";

/** A chord sheet, using almost all of the screen. Only the key is always shown; everything else opens when needed. */
export const ChordSheetScreen = observer(function ChordSheetScreen({ chordSheetId }: { chordSheetId: string }) {
    const { viewer, display, navigation, library, editor, settings } = useStores();
    const from = viewer.from;
    const { previous, next, position } = viewer.neighbours;
    const loaded = viewer.chordSheetId === chordSheetId;
    const inSetlist = from.kind === "setlist";
    const kind = inSetlist ? "song" : "version";
    const menuName = inSetlist ? viewer.setlist?.name : "All songs";
    const displayOpen = viewer.panel === "display";
    const menuLabel = inSetlist ? `Setlist ${menuName}` : menuName;
    const empty = viewer.sheet.sections.length === 0;
    const edit = () => editor.start(chordSheetId, from, empty ? "edit" : "arrange");
    const holdChord = useHoldChord(edit);

    const neighbours = (previous || next) && (
        <Neighbours aria-label={`Other ${kind}s`}>
            {previous && (
                <Neighbour onClick={() => navigation.replace(previous.screen)}>
                    <NeighbourKind>Previous {kind}</NeighbourKind>
                    {previous.title}
                </Neighbour>
            )}
            {next && (
                <Neighbour $next onClick={() => navigation.replace(next.screen)}>
                    <NeighbourKind>Next {kind}</NeighbourKind>
                    {next.title}
                </Neighbour>
            )}
        </Neighbours>
    );

    useEffect(() => {
        void viewer.load(chordSheetId);
    }, [viewer, chordSheetId]);

    return (
        <Screen>
            <Bar>
                <MenuButton aria-label={menuLabel} aria-expanded={viewer.panel === "menu"} onClick={() => viewer.toggle("menu")}>
                    <Icon name={inSetlist ? "setlist" : "songs"} />
                    <span>{menuName}</span>
                </MenuButton>
                <Title>{loaded ? library.title(chordSheetId) : ""}</Title>
                <Position>{position}</Position>
                {viewer.sheet.key && <KeyBadge aria-label={`Key of ${viewer.sheet.key}`}>{viewer.sheet.key}</KeyBadge>}
                <IconButton aria-label="Display settings" aria-expanded={displayOpen} onClick={() => viewer.toggle("display")}>
                    <Icon name="display" />
                </IconButton>
            </Bar>
            <Body {...(settings.holdToArrange ? holdChord : {})}>
                {loaded &&
                    (empty ? (
                        <>
                            <EmptyNote>
                                This chord sheet is still empty. Open the display settings and choose Edit to add lyrics and chords.
                            </EmptyNote>
                            {neighbours}
                        </>
                    ) : (
                        <SheetView
                            columns
                            splitSections={display.splitSections}
                            sheet={viewer.sheet}
                            lyricsOnly={display.lyricsOnly}
                            textSize={display.textSize}
                        >
                            {neighbours}
                        </SheetView>
                    ))}
            </Body>
            {displayOpen && <DisplayPanel onEdit={edit} />}
            {viewer.panel === "menu" &&
                (from.kind === "setlist" ? <SetlistMenu setlistId={from.setlistId} index={from.index} /> : <SongTreeMenu />)}
        </Screen>
    );
});

const Screen = styled.div`
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const Bar = styled.header`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    padding-top: max(4px, env(safe-area-inset-top));
`;

const MenuButton = styled(Button)`
    padding: 0 10px;

    @media (max-width: 699px) {
        width: 44px;
        padding: 0;

        span {
            display: none;
        }
    }
`;

const Title = styled.h1`
    margin: 0 0 0 4px;
    font-size: 17px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
`;

const Position = styled.span`
    flex: 1;
    font-size: 13px;
    color: ${color.textMuted};
    white-space: nowrap;
`;

/** As tall as the screen allows. The sheet goes on sideways in columns, so it doesn't scroll down. */
const Body = styled.main`
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 12px 16px 32px;

    @media (min-width: 700px) {
        padding: 16px 48px 40px;
    }
`;

const EmptyNote = styled.p`
    font-size: 17px;
    color: ${color.textSoft};
`;

const Neighbours = styled.nav`
    display: flex;
    gap: 10px;
    margin-top: 8px;
`;

const Neighbour = styled.button<{ $next?: boolean }>`
    flex: 1;
    min-height: 56px;
    padding: 6px 14px;
    border: 1px solid ${color.border};
    border-radius: 12px;
    background: ${color.background};
    text-align: ${({ $next }) => ($next ? "right" : "left")};
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    font-size: 15px;
    font-weight: 600;
`;

const NeighbourKind = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: ${color.textMuted};
`;
